import React, { useEffect, useMemo, useState } from "react";
import "@/App.css";
import {
  registerScientist, getScientistProfile, createEntry,
  getGlobalFeed, getLeaderboard, getGlobalStats, getDailyMission, getGenres,
} from "@/lib/api";
import { getLevelInfo, getLoopStatus } from "@/lib/levels";
import GenrePlanetSelector from "@/components/GenrePlanetSelector";
import AskSeanModal from "@/components/AskSeanModal";
import MisinfoBusterModal from "@/components/MisinfoBusterModal";
import BookRecommenderModal from "@/components/BookRecommenderModal";
import LeaderboardModal from "@/components/LeaderboardModal";
import SeansVideos from "@/components/SeansVideos";
import SpeedReaderModal from "@/components/SpeedReaderModal";
import InstallAppButton from "@/components/InstallAppButton";
import BookingSection from "@/components/BookingSection";
import SeanWorldSection from "@/components/SeanWorldSection";
import SeanChatbot from "@/components/SeanChatbot";
import FirstVisitInstallPrompt from "@/components/FirstVisitInstallPrompt";
import InviteButton from "@/components/InviteButton";
import TesterCountdownBanner from "@/components/TesterCountdownBanner";
import SneakPeekVault from "@/components/SneakPeekVault";
import MediaKitSection from "@/components/MediaKitSection";
import AdminInbox from "@/components/AdminInbox";
import SeanStudio from "@/components/SeanStudio";
import SeansLatestFeed from "@/components/SeansLatestFeed";
import HeroMediaCarousel from "@/components/HeroMediaCarousel";
import AutoUpdater from "@/components/AutoUpdater";
import { useSecretTaps, SecretStudioPopup } from "@/components/SecretStudioPopup";
import LabStories from "@/components/LabStories";
import SeanReels from "@/components/SeanReels";

const SLOGAN = "KEEP ON SCIENCING";
const SEAN_PHOTO = "/sean-portrait-new.jpg";
const SEAN_MERCH = "/sean-merch-new.jpg";
const SEAN_LOGO  = "/icon-192.png";
const SEAN_TED   = "/sean-ted-new.jpg";

const SEAN_QUOTES = [
  '"Reading is 99% curiosity."',
  '"The brain and heart are connected in a loop."',
  '"Credible. Clear. Concise."',
  '"Keep on sciencing!"',
];

/* ============= ONBOARDING ============= */
function Onboarding({ onDone, onClose }) {
  const [handle, setHandle] = useState("");
  const [name, setName] = useState("");
  const [ageYears, setAgeYears] = useState("");
  const [ageMonths, setAgeMonths] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    const h = handle.trim().toLowerCase().replace(/\s+/g, "_");
    if (!h) { setErr("Pick a lab name"); return; }
    setLoading(true);
    try {
      const y = Number(ageYears) || 0;
      const m = Number(ageMonths) || 0;
      const totalMonths = y * 12 + m;
      // Minimum age: 9 months (Sean started reading then!)
      const ageFloat = totalMonths > 0 ? Math.max(totalMonths, 9) / 12 : null;
      const sci = await registerScientist({ handle: h, display_name: name.trim() || h, age: ageFloat });
      localStorage.setItem("seanlab_handle", sci.handle);
      localStorage.setItem("seanlab_name", sci.display_name);
      localStorage.setItem("seanlab_session", `chat-${sci.handle}-${Date.now()}`);
      onDone(sci);
    } catch { setErr("Try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,18,48,0.55)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <div className="card max-w-md w-full animate-pop-in tint-sun overflow-hidden" style={{ padding: 0 }} onClick={(e) => e.stopPropagation()} data-testid="onboarding-card">
        {onClose && <button onClick={onClose} data-testid="onboarding-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white border-2 border-[var(--ink)] flex items-center justify-center text-lg z-20" style={{ boxShadow: "2px 2px 0 0 var(--ink)" }}>×</button>}
        {/* Lab banner — Sean in his lab with periodic table & beakers */}
        <div className="relative" style={{ borderBottom: "2.5px solid var(--ink)" }} data-testid="onboard-lab-banner">
          <img src="/sean-lab-new.jpg" alt="Sean in the lab — Keep on Sciencing!" className="w-full block" style={{ aspectRatio: "16/9", objectFit: "cover", objectPosition: "center 30%" }} />
          <div className="absolute inset-x-0 bottom-0" style={{ background: "linear-gradient(180deg, rgba(255,198,41,0) 0%, rgba(255,198,41,0.9) 80%)", padding: "1.2rem 1rem 0.5rem" }}>
            <div className="eyebrow text-[var(--red)] text-center">SEAN THE SCIENCE KID</div>
          </div>
        </div>
        <div className="text-center mb-4 px-5 pt-3">
          <h2 className="font-display text-3xl text-[var(--ink)] leading-tight">Join the Lab</h2>
          <p className="text-[var(--ink-soft)] text-sm mt-1">No email. No password. Just science.</p>
          <p className="text-[var(--ink-soft)] text-[0.7rem] mt-1 font-mono">Open to Sciencers from 9 months & up — Sean started reading at 9 months!</p>
        </div>
        <div className="space-y-3 px-5 pb-5">
          <div><label className="label-tag block mb-1">lab name *</label>
            <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="e.g., curious_leo" className="field font-mono" data-testid="onboard-handle" autoFocus />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="label-tag block mb-1">display</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Leo" className="field" data-testid="onboard-name" /></div>
            <div><label className="label-tag block mb-1">years</label><input type="number" min={0} max={120} value={ageYears} onChange={(e) => setAgeYears(e.target.value)} placeholder="10" className="field font-mono" data-testid="onboard-age-years" /></div>
            <div><label className="label-tag block mb-1">months</label><input type="number" min={0} max={11} value={ageMonths} onChange={(e) => setAgeMonths(e.target.value)} placeholder="0" className="field font-mono" data-testid="onboard-age-months" /></div>
          </div>
          {err && <div className="text-[var(--red)] font-bold">⚠ {err}</div>}
          <button onClick={submit} disabled={loading} className="btn btn-red w-full text-base" data-testid="onboard-submit">{loading ? "🔬 ..." : "🧪 Start My Lab"}</button>
        </div>
      </div>
    </div>
  );
}

/* ============= TOP NAV ============= */
function TopNav({ isOnboarded, scientist, onJoin, onSprint, onLeaderboard }) {
  const secret = useSecretTaps();
  return (
    <header className="sticky top-0 z-30 topnav" style={{ background: "rgba(255,249,237,0.92)", backdropFilter: "blur(12px)", borderBottom: "2.5px solid var(--ink)" }}>
      <SecretStudioPopup open={secret.open} onClose={() => secret.setOpen(false)} />
      <div className="max-w-7xl mx-auto flex items-stretch justify-between px-3 sm:px-6 lg:px-10 gap-2 sm:gap-3 topnav-inner">
        <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); secret.onTap(); }} className="flex items-center gap-2 sm:gap-3 no-underline h-full min-w-0" data-testid="header-logo-link">
          <img src={SEAN_LOGO} alt="Sean The Science Kid" className="h-full w-auto rounded-full border-[2.5px] border-[var(--ink)] object-cover block shrink-0" style={{ boxShadow: "3px 3px 0 0 var(--ink)" }} data-testid="header-logo" />
          <div className="leading-tight min-w-0">
            <div className="font-display text-base sm:text-xl text-[var(--ink)] truncate">Sean The Science Kid</div>
            <div className="eyebrow text-[var(--red)] hidden sm:block">⚡ {SLOGAN}</div>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-1">
          <button onClick={onSprint} className="px-3 py-1.5 rounded-full font-bold text-sm text-[var(--ink)] hover:bg-[var(--paper-2)]" data-testid="nav-sprint">⚡ Speed Race</button>
          <a href="#log" className="px-3 py-1.5 rounded-full font-bold text-sm text-[var(--ink)] hover:bg-[var(--paper-2)]">My Lab</a>
          <a href="#sean-videos" className="px-3 py-1.5 rounded-full font-bold text-sm text-[var(--ink)] hover:bg-[var(--paper-2)]">Videos</a>
          <a href="#sean-world" className="px-3 py-1.5 rounded-full font-bold text-sm text-[var(--ink)] hover:bg-[var(--paper-2)]" data-testid="nav-world">🌐 Site & Shop</a>
          <a
            href="#press"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("press");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="px-3 py-1.5 rounded-full font-bold text-sm text-[var(--ink)] hover:bg-[var(--paper-2)]"
            data-testid="nav-press"
          >
            📰 Press
          </a>
          <button onClick={onLeaderboard} className="px-3 py-1.5 rounded-full font-bold text-sm text-[var(--ink)] hover:bg-[var(--paper-2)]" data-testid="nav-leaderboard">Leaderboard</button>
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <InviteButton variant="header" handle={scientist?.handle} />
          <InstallAppButton variant="header" />
          {isOnboarded
            ? <div className="pill pill-sun text-[0.7rem] sm:text-xs px-2.5 sm:px-3 max-w-[120px] truncate" data-testid="user-chip">🧑‍🔬 @{scientist.handle}</div>
            : <button onClick={onJoin} className="btn btn-red text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2" data-testid="nav-join"><span className="sm:hidden">Join</span><span className="hidden sm:inline">Join Lab</span></button>}
        </div>
      </div>
    </header>
  );
}

/* ============= HERO ============= */
function Hero({ stats, onSprint, onJoin, isOnboarded, handle }) {
  return (
    <section id="top" className="relative px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-10 sm:pb-12">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-6 sm:gap-8 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <div className="pill pill-red mb-4 sm:mb-5 text-[0.65rem] sm:text-xs">⚡ LIVE · SUMMER 2026 · {SLOGAN}</div>
          <h1 className="font-display text-[2.05rem] sm:text-5xl lg:text-7xl text-[var(--ink)] leading-[0.95] mb-3 sm:mb-4">
            How <span style={{ color: "var(--red)" }}>FAST</span> can <span style={{ color: "var(--navy)" }}>you</span> read?
          </h1>
          <p className="text-[var(--ink-soft)] text-base sm:text-xl leading-relaxed max-w-xl mb-5 sm:mb-6">
            Race the clock with Sean's <strong style={{ color: "var(--red)" }}>Speed Reader</strong>. Read fast, read smart — your <strong>WPM</strong> goes on the global leaderboard. Built around Sean Atitsogbe — the 11-year-old genius, Mensa member, TED speaker, sharing science with the world.
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2.5 sm:gap-3 mb-5 sm:mb-6 hero-cta">
            <button onClick={onSprint} className="btn btn-red text-base sm:text-lg w-full sm:w-auto" data-testid="hero-sprint">⚡ Start a Speed Race</button>
            {!isOnboarded
              ? <button onClick={onJoin} className="btn btn-primary w-full sm:w-auto" data-testid="hero-join">🧪 Join the Lab — free</button>
              : <a href="#log" className="btn-ghost w-full sm:w-auto text-center">📓 My Lab</a>}
            <a href="#sean-videos" className="btn-ghost w-full sm:w-auto text-center">📺 Watch Sean</a>
            <a
              href="#press"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("press")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="btn-ghost w-full sm:w-auto text-center"
              data-testid="hero-press"
            >
              📰 Press Kit
            </a>
            <span className="hidden sm:inline-flex"><InstallAppButton variant="inline" /></span>
            <span className="hidden sm:inline-flex"><InviteButton variant="inline" handle={handle} /></span>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3 max-w-xl">
            <Stat label="Sciencers" value={stats?.community_size?.toLocaleString() || "1,500,000+"} tint="tint-orange" />
            <Stat label="books logged" value={(stats?.unique_books_read ?? 0).toLocaleString()} tint="tint-sun" />
            <Stat label="misinfo busted" value={(stats?.fact_checked_books ?? 0).toLocaleString()} tint="tint-blue" />
          </div>
        </div>

        {/* Sean's real photo — original orange frame, BIG cartoon avatar-style doodles around his head */}
        <div className="lg:col-span-5 order-1 lg:order-2 relative">
          <div className="relative tint-orange card-flat" style={{ borderRadius: "1.75rem", padding: "0.65rem" }}>
            <div className="relative overflow-hidden" style={{ borderRadius: "1rem", border: "2.5px solid var(--ink)", boxShadow: "3px 3px 0 0 var(--ink)", aspectRatio: "4 / 5", background: "#0e1230" }}>
              <HeroMediaCarousel photoSrc={SEAN_PHOTO}>
              {/* BIG cartoon doodles — all clustered around HIS HEAD (upper portion of image) */}
              {/* Beaker — top-left corner above head */}
              <svg aria-hidden viewBox="0 0 100 100" className="absolute animate-float" style={{ top: "0%", left: "-2%", width: "22%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", zIndex: 2 }}>
                <path d="M36 12 L36 38 L18 78 Q14 92 30 92 L70 92 Q86 92 82 78 L64 38 L64 12 Z" fill="#7ed4ff" stroke="#0e1230" strokeWidth="4" strokeLinejoin="round"/>
                <path d="M30 12 L70 12" stroke="#0e1230" strokeWidth="4" strokeLinecap="round"/>
                <path d="M22 70 Q50 60 78 70 L78 80 Q50 86 22 80 Z" fill="#2eb4d6" stroke="#0e1230" strokeWidth="3"/>
                <circle cx="38" cy="72" r="3.5" fill="#fff" opacity="0.85"/>
                <circle cx="56" cy="68" r="2.5" fill="#fff" opacity="0.85"/>
                <circle cx="48" cy="78" r="2" fill="#fff" opacity="0.85"/>
              </svg>

              {/* Molecule — top-right corner above head */}
              <svg aria-hidden viewBox="0 0 100 100" className="absolute animate-wiggle" style={{ top: "0%", right: "-2%", width: "22%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", animationDelay: "0.5s", zIndex: 2 }}>
                <line x1="22" y1="28" x2="50" y2="50" stroke="#0e1230" strokeWidth="4.5"/>
                <line x1="50" y1="50" x2="80" y2="28" stroke="#0e1230" strokeWidth="4.5"/>
                <line x1="50" y1="50" x2="48" y2="82" stroke="#0e1230" strokeWidth="4.5"/>
                <line x1="50" y1="50" x2="20" y2="75" stroke="#0e1230" strokeWidth="4.5"/>
                <circle cx="22" cy="28" r="11" fill="#ff5147" stroke="#0e1230" strokeWidth="3.5"/>
                <circle cx="80" cy="28" r="11" fill="#ffd93d" stroke="#0e1230" strokeWidth="3.5"/>
                <circle cx="48" cy="82" r="11" fill="#7ed4ff" stroke="#0e1230" strokeWidth="3.5"/>
                <circle cx="20" cy="75" r="9"  fill="#14b87a" stroke="#0e1230" strokeWidth="3.5"/>
                <circle cx="50" cy="50" r="13" fill="#ff8a3d" stroke="#0e1230" strokeWidth="3.5"/>
              </svg>

              {/* BIG BRAIN — beside his head on the LEFT */}
              <svg aria-hidden viewBox="0 0 100 100" className="absolute animate-float" style={{ top: "12%", left: "-5%", width: "26%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", animationDelay: "0.15s", zIndex: 2 }}>
                {/* Brain outline — two hemispheres */}
                <path d="M48 14 Q34 8 24 18 Q12 22 12 36 Q4 44 10 56 Q6 68 18 74 Q22 86 36 86 Q44 92 48 86 L48 14 Z" fill="#ff8fb3" stroke="#0e1230" strokeWidth="4" strokeLinejoin="round"/>
                <path d="M52 14 Q66 8 76 18 Q88 22 88 36 Q96 44 90 56 Q94 68 82 74 Q78 86 64 86 Q56 92 52 86 L52 14 Z" fill="#ff5c93" stroke="#0e1230" strokeWidth="4" strokeLinejoin="round"/>
                <line x1="50" y1="14" x2="50" y2="86" stroke="#0e1230" strokeWidth="3.5"/>
                <path d="M22 28 Q32 32 28 42 Q24 50 32 56 Q26 64 32 72" fill="none" stroke="#0e1230" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M16 44 Q22 48 20 56" fill="none" stroke="#0e1230" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M78 28 Q68 32 72 42 Q76 50 68 56 Q74 64 68 72" fill="none" stroke="#0e1230" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M84 44 Q78 48 80 56" fill="none" stroke="#0e1230" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>

              {/* BIG HEART — beside his head on the RIGHT */}
              <svg aria-hidden viewBox="0 0 100 100" className="absolute animate-heartbeat" style={{ top: "12%", right: "-5%", width: "26%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", animationDelay: "0.35s", zIndex: 2 }}>
                <path d="M50 88 L14 50 Q4 38 12 24 Q22 10 38 16 Q46 20 50 28 Q54 20 62 16 Q78 10 88 24 Q96 38 86 50 Z" fill="#ff3e5c" stroke="#0e1230" strokeWidth="4.5" strokeLinejoin="round"/>
                <path d="M24 28 Q20 36 26 42" fill="none" stroke="#ffe4ea" strokeWidth="4" strokeLinecap="round"/>
                <path d="M18 60 L30 60 L34 50 L40 70 L46 54 L52 64 L82 64" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>

              {/* MICROSCOPE — centered above his head, proper cartoon style */}
              <svg aria-hidden viewBox="0 0 100 100" className="absolute animate-float" style={{ top: "-4%", left: "40%", width: "20%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", animationDelay: "0.45s", zIndex: 2 }}>
                {/* Base — wide trapezoid at the bottom */}
                <path d="M14 96 L86 96 L78 84 L22 84 Z" fill="#0e1230" stroke="#0e1230" strokeWidth="3" strokeLinejoin="round"/>
                {/* Stage — horizontal flat platform */}
                <rect x="22" y="58" width="56" height="6" fill="#9aa1b5" stroke="#0e1230" strokeWidth="3"/>
                {/* Slide — tiny glass slide on the stage */}
                <rect x="34" y="55" width="32" height="3" fill="#7ed4ff" stroke="#0e1230" strokeWidth="2"/>
                {/* Arm — curved C-shape connecting base to head */}
                <path d="M30 84 L30 64 Q30 50 50 50" fill="none" stroke="#cdd3e6" strokeWidth="10" strokeLinecap="round"/>
                <path d="M30 84 L30 64 Q30 50 50 50" fill="none" stroke="#0e1230" strokeWidth="3" strokeLinecap="round"/>
                {/* Focus knob — yellow circle on the arm */}
                <circle cx="30" cy="74" r="6" fill="#ffd93d" stroke="#0e1230" strokeWidth="3"/>
                {/* Objective lens / nose piece — orange cylinder hanging down toward the stage */}
                <rect x="44" y="42" width="14" height="14" fill="#ff8a3d" stroke="#0e1230" strokeWidth="3" rx="2"/>
                <circle cx="51" cy="56" r="3" fill="#0e1230"/>
                {/* Eyepiece tube — going up from the nose piece */}
                <rect x="45" y="20" width="12" height="22" fill="#cdd3e6" stroke="#0e1230" strokeWidth="3"/>
                {/* Eyepiece top — the cylinder at the very top, where you look into */}
                <rect x="42" y="10" width="18" height="12" fill="#ff5147" stroke="#0e1230" strokeWidth="3" rx="2"/>
                {/* Eyepiece ring */}
                <rect x="42" y="18" width="18" height="3" fill="#0e1230"/>
              </svg>

              {/* SUN — BIG, cheery face, top-center-left */}
              <svg aria-hidden viewBox="0 0 100 100" className="absolute animate-spin-slow" style={{ top: "-1%", left: "27%", width: "14%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", zIndex: 2 }}>
                <g stroke="#0e1230" strokeWidth="3.5" strokeLinecap="round">
                  <line x1="50" y1="4"  x2="50" y2="18"/>
                  <line x1="50" y1="82" x2="50" y2="96"/>
                  <line x1="4"  y1="50" x2="18" y2="50"/>
                  <line x1="82" y1="50" x2="96" y2="50"/>
                  <line x1="14" y1="14" x2="24" y2="24"/>
                  <line x1="76" y1="76" x2="86" y2="86"/>
                  <line x1="86" y1="14" x2="76" y2="24"/>
                  <line x1="14" y1="86" x2="24" y2="76"/>
                </g>
                <circle cx="50" cy="50" r="28" fill="#ffd93d" stroke="#0e1230" strokeWidth="4"/>
                <circle cx="42" cy="46" r="3" fill="#0e1230"/>
                <circle cx="58" cy="46" r="3" fill="#0e1230"/>
                <path d="M40 56 Q50 64 60 56" fill="none" stroke="#0e1230" strokeWidth="3" strokeLinecap="round"/>
              </svg>

              {/* ROCKET — flying angled near top-right */}
              <svg aria-hidden viewBox="0 0 100 100" className="absolute animate-float" style={{ top: "0%", right: "20%", width: "12%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", transform: "rotate(20deg)", animationDelay: "0.55s", zIndex: 2 }}>
                <path d="M50 6 Q72 28 70 62 L70 80 L30 80 L30 62 Q28 28 50 6 Z" fill="#fff" stroke="#0e1230" strokeWidth="4" strokeLinejoin="round"/>
                <circle cx="50" cy="36" r="9" fill="#7ed4ff" stroke="#0e1230" strokeWidth="3.5"/>
                <circle cx="46" cy="33" r="2.5" fill="#fff" opacity="0.8"/>
                <path d="M30 62 L14 84 L30 80 Z" fill="#ff3e5c" stroke="#0e1230" strokeWidth="3.5" strokeLinejoin="round"/>
                <path d="M70 62 L86 84 L70 80 Z" fill="#ff3e5c" stroke="#0e1230" strokeWidth="3.5" strokeLinejoin="round"/>
                <path d="M30 80 L70 80" stroke="#0e1230" strokeWidth="3"/>
                <path d="M42 80 L46 95 L50 84 L54 95 L58 80 Z" fill="#ff8a3d" stroke="#0e1230" strokeWidth="3" strokeLinejoin="round"/>
              </svg>

              {/* Gear — top spot between beaker & sun */}
              <svg aria-hidden viewBox="0 0 100 100" className="absolute animate-spin-slow" style={{ top: "2%", left: "20%", width: "10%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", zIndex: 2 }}>
                <path d="M50 8 L57 18 L70 14 L70 28 L82 32 L78 45 L88 55 L78 65 L82 78 L70 82 L70 96 L57 92 L50 102 L43 92 L30 96 L30 82 L18 78 L22 65 L12 55 L22 45 L18 32 L30 28 L30 14 L43 18 Z" fill="#ff8a3d" stroke="#0e1230" strokeWidth="3.5" strokeLinejoin="round"/>
                <circle cx="50" cy="55" r="13" fill="#fff5e6" stroke="#0e1230" strokeWidth="3"/>
              </svg>

              {/* Lightbulb — BIGGER, top spot between rocket & molecule */}
              <svg aria-hidden viewBox="0 0 100 100" className="absolute animate-wiggle" style={{ top: "1%", right: "8%", width: "14%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", animationDelay: "0.8s", zIndex: 2 }}>
                <path d="M35 15 Q50 0 65 15 Q80 30 70 50 L65 70 L35 70 L30 50 Q20 30 35 15 Z" fill="#ffd93d" stroke="#0e1230" strokeWidth="4"/>
                <rect x="38" y="72" width="24" height="8" fill="#888" stroke="#0e1230" strokeWidth="3"/>
                <rect x="40" y="82" width="20" height="6" fill="#666" stroke="#0e1230" strokeWidth="3"/>
                <path d="M50 30 L50 60 M40 40 L60 40 M40 55 L60 55" stroke="#0e1230" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>

              {/* Atom — bottom-left, big & bold */}
              <svg aria-hidden viewBox="0 0 100 100" className="absolute animate-wiggle" style={{ bottom: "18%", left: "-3%", width: "26%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", animationDelay: "0.3s", zIndex: 2 }}>
                <ellipse cx="50" cy="50" rx="44" ry="17" fill="none" stroke="#0e1230" strokeWidth="5"/>
                <ellipse cx="50" cy="50" rx="44" ry="17" fill="none" stroke="#0e1230" strokeWidth="5" transform="rotate(60 50 50)"/>
                <ellipse cx="50" cy="50" rx="44" ry="17" fill="none" stroke="#0e1230" strokeWidth="5" transform="rotate(-60 50 50)"/>
                <circle cx="50" cy="50" r="10" fill="#ff5147" stroke="#0e1230" strokeWidth="3.5"/>
                <circle cx="14" cy="40" r="5"  fill="#ff8a3d" stroke="#0e1230" strokeWidth="3"/>
                <circle cx="86" cy="62" r="5"  fill="#ffd93d" stroke="#0e1230" strokeWidth="3"/>
                <circle cx="68" cy="20" r="4.5" fill="#7ed4ff" stroke="#0e1230" strokeWidth="3"/>
              </svg>

              {/* Globe — BIGGER, moved UP, tilted, over his left shoulder */}
              <svg aria-hidden viewBox="0 0 100 110" className="absolute animate-float" style={{ top: "32%", right: "-5%", width: "28%", height: "auto", filter: "drop-shadow(3px 3px 0 #0e1230)", transform: "rotate(-14deg)", animationDelay: "0.7s", zIndex: 2 }}>
                {/* Globe sphere */}
                <circle cx="50" cy="46" r="40" fill="#3aa9e8" stroke="#0e1230" strokeWidth="4"/>
                {/* Ocean shading band */}
                <ellipse cx="50" cy="46" rx="40" ry="14" fill="none" stroke="#1d7fb8" strokeWidth="2" opacity="0.6"/>
                {/* Africa-ish continent (center-right) */}
                <path d="M48 22 Q60 24 62 36 Q66 46 60 58 Q58 70 50 72 Q44 64 44 56 Q40 48 44 38 Q46 28 48 22 Z" fill="#34c97b" stroke="#0e1230" strokeWidth="2.5" strokeLinejoin="round"/>
                {/* South America-ish (left lower) */}
                <path d="M22 42 Q30 40 32 50 Q34 60 28 68 Q22 70 20 62 Q18 52 22 42 Z" fill="#34c97b" stroke="#0e1230" strokeWidth="2.5" strokeLinejoin="round"/>
                {/* North America-ish (left upper) */}
                <path d="M18 24 Q28 20 34 28 Q32 36 24 38 Q16 34 18 24 Z" fill="#34c97b" stroke="#0e1230" strokeWidth="2.5" strokeLinejoin="round"/>
                {/* Asia/Australia-ish (right) */}
                <path d="M70 28 Q82 26 84 36 Q78 42 70 38 Z M74 56 Q82 56 84 64 Q80 70 72 66 Z" fill="#34c97b" stroke="#0e1230" strokeWidth="2.5" strokeLinejoin="round"/>
                {/* Latitude / longitude curves */}
                <ellipse cx="50" cy="46" rx="40" ry="40" fill="none" stroke="#0e1230" strokeWidth="2" opacity="0.7" transform="rotate(-18 50 46)"/>
                {/* Shine highlight */}
                <path d="M28 20 Q22 30 26 40" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" opacity="0.7"/>
                {/* Stand — arc + base */}
                <path d="M10 46 Q10 92 50 92 Q90 92 90 46" fill="none" stroke="#0e1230" strokeWidth="4" strokeLinecap="round"/>
                <rect x="40" y="92" width="20" height="6" fill="#ffd93d" stroke="#0e1230" strokeWidth="3"/>
                <rect x="32" y="98" width="36" height="8" fill="#ff8a3d" stroke="#0e1230" strokeWidth="3" rx="2"/>
              </svg>

              {/* Full-bleed blue band — touches both image edges */}
              <div
                aria-hidden
                className="absolute"
                style={{
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "linear-gradient(180deg, rgba(20,38,107,0) 0%, rgba(20,38,107,0.96) 28%, #14266b 60%)",
                  paddingTop: "3rem",
                  paddingBottom: "0.9rem",
                  textAlign: "center",
                  zIndex: 3,
                }}
              >
                <div
                  style={{
                    color: "#fff",
                    fontFamily: "'Lilita One', sans-serif",
                    fontSize: "clamp(0.95rem, 2.3vw, 1.5rem)",
                    lineHeight: 1.06,
                    letterSpacing: "0.012em",
                    textShadow: "2px 2px 0 rgba(0,0,0,0.42)",
                    padding: "0 4%",
                  }}
                >
                  11-YEAR-OLD GENIUS<br/>
                  SHARING SCIENCE WITH THE WORLD!
                </div>
              </div>
              </HeroMediaCarousel>
            </div>
            <span className="absolute -top-3 -left-3 pill pill-red animate-wiggle" style={{ zIndex: 5 }}>🧠 Mensa @ 4</span>
            <span className="absolute -bottom-3 -right-3 pill pill-green animate-float" style={{ zIndex: 5 }}>🎓 TED speaker</span>
          </div>
          <a href="https://www.instagram.com/seanthesciencekidofficial?rpxt=MWw2NjJjeXR2bW55dg%3D%3D&utm_source=qr" target="_blank" rel="noreferrer noopener" className="mt-3 flex items-center justify-center gap-2 font-fun text-sm text-[var(--ink-soft)] hover:text-[var(--red)]" data-testid="instagram-link">
            📸 follow @seanthesciencekidofficial — 1.5M Sciencers strong
          </a>
        </div>
      </div>
    </section>
  );
}

function compact(n) {
  if (typeof n !== "number") return n;
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1) + "M";
  if (n >= 10_000) return Math.round(n / 1000) + "k";
  return n.toLocaleString();
}

function Stat({ label, value, tint }) {
  // value comes in pre-formatted as string; also compute a compact mobile-friendly version
  const raw = typeof value === "string" ? Number(value.replace(/[^\d]/g, "")) : value;
  const mobileValue = Number.isFinite(raw) && raw >= 10_000 ? compact(raw) : value;
  return (
    <div className={`card-flat ${tint}`} style={{ padding: "0.7rem 0.75rem", minWidth: 0 }}>
      <div className="stat-num text-[var(--ink)] leading-none truncate sm:hidden text-2xl" title={String(value)}>{mobileValue}</div>
      <div className="stat-num text-[var(--ink)] leading-none hidden sm:block sm:text-3xl">{value}</div>
      <div className="label-tag mt-1 text-[0.58rem] sm:text-[0.68rem] truncate">{label}</div>
    </div>
  );
}

/* ============= QUOTES ============= */
function QuotesMarquee() {
  const items = SEAN_QUOTES.flatMap((q) => [q, `⚡ ${SLOGAN} ⚡`]);
  const doubled = [...items, ...items];
  return (
    <div className="relative overflow-hidden py-3.5" style={{ background: "var(--red)", borderTop: "2.5px solid var(--ink)", borderBottom: "2.5px solid var(--ink)" }}>
      <div className="flex gap-10 animate-marquee whitespace-nowrap" style={{ width: "max-content" }}>
        {doubled.map((q, i) => {
          const isSlogan = q.includes(SLOGAN);
          return (
            <span key={i} className={`font-display text-lg sm:text-xl ${isSlogan ? "text-[var(--sun)]" : "text-white"}`}>
              <span className="mr-3 opacity-80">✦</span>{q}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* ============= LAB STATUS STRIP ============= */
function YourLabStrip({ profile }) {
  if (!profile) return null;
  const s = profile.stats || {};
  const xp = (s.total_brain || 0) + (s.total_heart || 0) * 10;
  const { level, next, progress } = getLevelInfo(xp);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-8" data-testid="lab-strip">
      <div className="card tint-cream">
        <div className="grid lg:grid-cols-12 gap-5 items-center">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4">
              <div className="text-5xl animate-float">{level.emoji}</div>
              <div className="flex-1">
                <div className="eyebrow text-[var(--red)]">your level</div>
                <div className="font-display text-2xl text-[var(--ink)]">{level.name}</div>
                <div className="mt-2 xpbar-track"><div className="xpbar-fill" style={{ width: `${Math.round(progress * 100)}%` }} /></div>
                <div className="flex justify-between mt-1.5 text-xs font-mono">
                  <span className="font-bold text-[var(--ink)]">{xp.toLocaleString()} XP</span>
                  {next ? <span className="text-[var(--ink-soft)]">next · {next.emoji} {next.name}</span> : <span className="text-[var(--red)]">👑 max</span>}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Micro emoji="🧠" label="brain" value={s.total_brain || 0} tint="tint-pink" />
            <Micro emoji="🫀" label="heart" value={s.total_heart || 0} tint="tint-red" />
            <Micro emoji="🧪" label="entries" value={s.entries_count || 0} tint="tint-blue" />
            <Micro emoji="🔥" label="streak" value={(s.streak_days || 0) + "d"} tint="tint-sun" animate={(s.streak_days || 0) >= 1} />
          </div>
        </div>
      </div>
    </section>
  );
}

function Micro({ emoji, label, value, tint, animate }) {
  return (
    <div className={`card-flat ${tint}`} data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xl ${animate ? "animate-flame" : ""}`}>{emoji}</span>
        <span className="label-tag">{label}</span>
      </div>
      <div className="stat-num text-2xl text-[var(--ink)]">{typeof value === "number" ? value.toLocaleString() : value}</div>
    </div>
  );
}

/* ============= LAB NOTEBOOK ============= */
function LabNotebookCard({ scientist, genres, onLogged }) {
  const [book, setBook] = useState("");
  const [genreKey, setGenreKey] = useState("sun");
  const [minutes, setMinutes] = useState(15);
  const [words, setWords] = useState(800);
  const [fact, setFact] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const g = genres.find((x) => x.key === genreKey) || { mult: 1 };
  const previewBrain = Math.floor(Number(words || 0) * g.mult);
  const previewHeart = Number(minutes || 0);
  const status = getLoopStatus(previewBrain, previewHeart);

  const submit = async () => {
    setError("");
    if (!Number(minutes) && !Number(words)) { setError("Add minutes or words!"); return; }
    setBusy(true);
    try {
      const entry = await createEntry({ scientist_handle: scientist.handle, book: book || "Untitled Reading", genre_key: genreKey, minutes: Number(minutes) || 0, words: Number(words) || 0, fact_checked: fact });
      setPreview(entry); setBook(""); setMinutes(15); setWords(800); setFact(false);
      onLogged && onLogged(entry);
      setTimeout(() => setPreview(null), 4500);
    } catch { setError("Try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" data-testid="lab-notebook">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="eyebrow text-[var(--red)]">📓 lab notebook</div>
          <div className="font-display text-3xl text-[var(--ink)] leading-tight mt-1">Log Today's Reading</div>
          <div className="label-tag mt-1">Sciencer · @{scientist.handle}</div>
        </div>
      </div>
      <div className="space-y-4">
        <div><label className="label-tag block mb-1.5">book · article · podcast</label><input value={book} onChange={(e) => setBook(e.target.value)} placeholder="What did you read?" className="field" data-testid="entry-book" /></div>
        <div><label className="label-tag block mb-2">pick a genre planet</label><GenrePlanetSelector genres={genres} value={genreKey} onChange={setGenreKey} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-tag block mb-1.5" style={{ color: "var(--red)" }}>🫀 minutes</label><input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} className="field font-mono" data-testid="entry-minutes" /></div>
          <div><label className="label-tag block mb-1.5" style={{ color: "var(--navy)" }}>🧠 words</label><input type="number" value={words} onChange={(e) => setWords(e.target.value)} className="field font-mono" data-testid="entry-words" /></div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl tint-sun" style={{ border: "2px solid var(--ink)" }}>
          <input type="checkbox" checked={fact} onChange={(e) => setFact(e.target.checked)} className="checkbox-modern" data-testid="entry-fact-check" />
          <span className="text-sm font-bold text-[var(--ink)]">🔍 I fact-checked this — Clickbait Slayer!</span>
        </label>
        <div className="card-flat tint-cream">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex gap-5">
              <div><div className="label-tag">brain</div><div className="stat-num text-2xl text-[var(--pink)]">{previewBrain.toLocaleString()}</div></div>
              <div><div className="label-tag">heart</div><div className="stat-num text-2xl text-[var(--red)]">{previewHeart}</div></div>
            </div>
            <div className="pill" style={{ background: status.color, color: status.color === "#fff" ? "var(--ink)" : "var(--ink)" }}>{status.emoji} {status.text}</div>
          </div>
        </div>
        {error && <div className="text-[var(--red)] font-bold text-sm">⚠ {error}</div>}
        <button onClick={submit} disabled={busy} className="btn btn-red w-full" data-testid="entry-submit">{busy ? "📡 ..." : "🧪 Record My Experiment"}</button>
        {preview && <div className="animate-pop-in card-flat tint-green text-sm font-bold" data-testid="entry-success">✅ +{preview.brain_points} brain · +{preview.heart_points} heart{preview.loop_balanced && " · ⚖️ BALANCED!"} · keep on sciencing!</div>}
      </div>
    </div>
  );
}

/* ============= TOOLS ============= */
function ToolsSection({ onSprint, onAsk, onMisinfo, onBooks, onLeaderboard }) {
  const tools = [
    { label: "Speed Race",       desc: "Fastest reader wins!",       emoji: "⚡", tint: "tint-red",    onClick: onSprint,      testId: "tool-sprint" },
    { label: "Ask Sean",         desc: "Chat with the Science Kid",  emoji: "💬", tint: "tint-pink",   onClick: onAsk,         testId: "tool-ask-sean" },
    { label: "Clickbait Slayer", desc: "Spot the fake fact",         emoji: "🔍", tint: "tint-blue",   onClick: onMisinfo,     testId: "tool-misinfo" },
    { label: "Book Lab",         desc: "AI book picks",              emoji: "📚", tint: "tint-green",  onClick: onBooks,       testId: "tool-books" },
    { label: "Leaderboard",      desc: "Top Sciencers",              emoji: "🏆", tint: "tint-sun",    onClick: onLeaderboard, testId: "tool-leaderboard" },
  ];
  return (
    <section id="tools" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-10">
      <div className="mb-5"><div className="eyebrow text-[var(--red)]">science tools</div><h2 className="font-display text-3xl sm:text-4xl text-[var(--ink)]">Play. Race. Discover.</h2></div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        {tools.map((t) => (
          <button key={t.label} onClick={t.onClick} data-testid={t.testId} className={`card card-hover ${t.tint} text-left`} style={{ padding: "1rem" }}>
            <div className="text-3xl sm:text-4xl mb-1.5 sm:mb-2 animate-float" style={{ animationDelay: `${Math.random()}s` }} aria-hidden>{t.emoji}</div>
            <div className="font-display text-base sm:text-lg text-[var(--ink)] leading-tight">{t.label}</div>
            <div className="text-[0.72rem] sm:text-xs text-[var(--ink-soft)] mt-1 sm:mt-1.5 leading-snug">{t.desc}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

/* ============= FEED + BADGES ============= */
const BADGE_CATALOG = [
  { emoji: "🧪", name: "First Observation", desc: "Log your first reading experiment" },
  { emoji: "🔬", name: "Lab Regular", desc: "5+ lab entries" },
  { emoji: "⚗️", name: "Senior Scientist", desc: "15+ lab entries" },
  { emoji: "🧠", name: "Brain Booster", desc: "5,000+ brain points" },
  { emoji: "🫀", name: "Heart Hustler", desc: "200+ heart minutes" },
  { emoji: "🧠🫀", name: "Neurocardio Apprentice", desc: "Brain & heart both leveled up" },
  { emoji: "⚖️", name: "Loop Balancer", desc: "3 balanced sessions" },
  { emoji: "🏅", name: "Neurocardio Master", desc: "10 perfectly balanced loops" },
  { emoji: "🔍", name: "Clickbait Slayer", desc: "Fact-check your first source" },
  { emoji: "✅", name: "Misinformation Buster", desc: "5 fact-checked resources" },
  { emoji: "🌌", name: "Gravitational Pull", desc: "Explore 3+ genre planets" },
  { emoji: "🪐", name: "Galactic Reader", desc: "5+ planets visited" },
  { emoji: "🚀", name: "Cosmic Explorer", desc: "7+ planets visited" },
  { emoji: "📖", name: "Hyperthymesia Hunter", desc: "Reread a book" },
  { emoji: "🔄", name: "Perfect Loop Harmony", desc: "Global brain/heart in balance" },
];

function BadgeWallBody({ badges }) {
  const earned = new Set(badges.map((b) => b.name));
  const extra = badges.filter((b) => !BADGE_CATALOG.some((c) => c.name === b.name));
  const all = [...BADGE_CATALOG, ...extra];
  const pct = Math.round((earned.size / all.length) * 100);
  return (
    <>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="eyebrow text-[var(--red)]">badge wall</div>
          <div className="font-display text-xl sm:text-2xl text-[var(--ink)]">Earned Badges</div>
        </div>
        <span className="pill pill-sun text-[0.65rem] sm:text-xs whitespace-nowrap shrink-0" data-testid="badge-count">{earned.size} / {all.length} unlocked</span>
      </div>
      <div className="h-2.5 rounded-full mb-4 overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ink)" }}>
        <div className="h-full transition-[width] duration-700" style={{ width: `${pct}%`, background: "var(--sun)" }} data-testid="badge-progress" />
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-2.5">
        {all.map((b, i) => {
          const got = earned.has(b.name);
          return (
            <div
              key={b.name}
              title={got ? b.desc : `Locked — ${b.desc}`}
              className="card-flat animate-pop-in flex flex-col items-center justify-center text-center min-w-0"
              style={{ padding: "0.55rem 0.35rem", background: got ? (i % 2 === 0 ? "var(--sun)" : "#fff") : "rgba(14,18,48,0.04)", opacity: got ? 1 : 0.55, animationDelay: `${i * 0.03}s` }}
              data-testid={`badge-${got ? "earned" : "locked"}-${i}`}
            >
              <span className="text-xl sm:text-2xl leading-none mb-1" style={{ filter: got ? "none" : "grayscale(1)" }}>{got ? b.emoji : "🔒"}</span>
              <span className="text-[0.62rem] sm:text-[0.7rem] font-semibold leading-tight text-[var(--ink)] line-clamp-2">{b.name}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

function FeedAndBadges({ feed, badges }) {
  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">
      {/* === BADGE WALL === */}
      <div className="lg:col-span-7 min-w-0">
        <div className="card h-full overflow-hidden" data-testid="badge-wall" style={{ wordBreak: "break-word" }}>
          <BadgeWallBody badges={badges} />
        </div>
      </div>

      {/* === LIVE FEED === */}
      <div className="lg:col-span-5 min-w-0">
        <div className="card tint-cream h-full overflow-hidden" data-testid="live-feed">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="min-w-0">
              <div className="eyebrow text-[var(--orange)]">📡 live</div>
              <div className="font-display text-xl sm:text-2xl text-[var(--ink)]">Global Lab Feed</div>
            </div>
            <span className="pill pill-green text-[0.65rem] sm:text-xs whitespace-nowrap shrink-0 inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[var(--ink)] animate-pulse"></span>LIVE
            </span>
          </div>
          <div className="space-y-2 max-h-[360px] sm:max-h-[440px] lg:max-h-[300px] overflow-y-auto pr-1 -mx-1 px-1">
            {feed.length === 0 && <div className="text-[var(--ink-soft)] text-sm text-center py-6">No entries yet — be first!</div>}
            {feed.map((e) => (
              <div key={e.id} className="card-flat min-w-0" style={{ wordBreak: "break-word" }}>
                <div className="font-fun text-sm text-[var(--ink)] truncate">🧑‍🔬 {e.scientist_name} <span className="label-tag font-normal">@{e.scientist_handle}</span></div>
                <div className="text-sm text-[var(--ink)] truncate">📖 <em>"{e.book}"</em> · {e.genre_name}</div>
                <div className="flex flex-wrap gap-2 sm:gap-3 mt-1 text-xs font-mono">
                  <span className="text-[var(--pink)]">🧠 {e.brain_points}</span>
                  <span className="text-[var(--red)]">🫀 {e.heart_points}</span>
                  {e.fact_checked && <span className="text-[var(--green)]">🔍 fact-checked</span>}
                  {e.loop_balanced && <span className="text-[var(--orange)]">⚖️ balanced</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ====================== APP ====================== */
function App() {
  const [scientist, setScientist] = useState(null);
  const [profile, setProfile] = useState(null);
  const [genres, setGenres] = useState([]);
  const [feed, setFeed] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [dailyMission, setDailyMission] = useState(null);
  const [showAsk, setShowAsk] = useState(false);
  const [showMisinfo, setShowMisinfo] = useState(false);
  const [showBooks, setShowBooks] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showVault, setShowVault] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);
  const [showSprint, setShowSprint] = useState(false);

  // ----- Admin route: hash- OR query-param-based, hidden from public nav -----
  // Accepts any of: #admin, ?admin=1, ?admin, /admin — bulletproof against PWA cache
  const isAdminUrl = () => {
    const hash = window.location.hash.toLowerCase();
    const path = window.location.pathname.toLowerCase();
    const search = window.location.search.toLowerCase();
    return (
      hash === "#admin" ||
      hash.startsWith("#admin") ||
      path === "/admin" ||
      path.endsWith("/admin") ||
      search.includes("admin=1") ||
      search.includes("admin=true") ||
      search === "?admin"
    );
  };
  const [adminMode, setAdminMode] = useState(isAdminUrl);
  // ----- Sean's Studio route: #sean / ?sean=1 -----
  const isSeanUrl = () => {
    const hash = window.location.hash.toLowerCase();
    const path = window.location.pathname.toLowerCase();
    const search = window.location.search.toLowerCase();
    return (
      hash === "#sean" ||
      hash === "#studio" ||
      path === "/sean" ||
      path.endsWith("/sean") ||
      path === "/studio" ||
      search.includes("sean=1") ||
      search.includes("studio=1")
    );
  };
  const [seanMode, setSeanMode] = useState(isSeanUrl);
  useEffect(() => {
    const onChange = () => { setAdminMode(isAdminUrl()); setSeanMode(isSeanUrl()); };
    window.addEventListener("hashchange", onChange);
    window.addEventListener("popstate", onChange);
    return () => {
      window.removeEventListener("hashchange", onChange);
      window.removeEventListener("popstate", onChange);
    };
  }, []);
  // NOTE: the conditional return for adminMode must come AFTER every hook below
  // — moved to just before the JSX return to satisfy React's rules-of-hooks.

  const sessionId = useMemo(() => localStorage.getItem("seanlab_session") || `chat-${Date.now()}`, [scientist]);

  useEffect(() => {
    const h = localStorage.getItem("seanlab_handle");
    if (h) getScientistProfile(h).then((p) => { setScientist(p.scientist); setProfile(p); }).catch(() => localStorage.removeItem("seanlab_handle"));
    getGenres().then(setGenres).catch(() => {});
    refreshGlobals();
    getDailyMission().then(setDailyMission).catch(() => {});
    const id = setInterval(refreshGlobals, 15000);

    // Honour PWA shortcut deep-links (?go=sprint | ?go=ask)
    try {
      const params = new URLSearchParams(window.location.search);
      const go = params.get("go");
      if (go === "sprint") setShowSprint(true);
      else if (go === "ask") setShowAsk(true);
      else if (go === "books") setShowBooks(true);
      else if (go === "vault") setShowVault(true);
      if (go) {
        params.delete("go");
        const q = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (q ? "?" + q : ""));
      }
    } catch {/* noop */}

    return () => clearInterval(id);
  }, []);

  const refreshGlobals = () => {
    getGlobalFeed(25).then(setFeed).catch(() => {});
    getLeaderboard(20).then(setLeaders).catch(() => {});
    getGlobalStats().then(setGlobalStats).catch(() => {});
  };
  const refreshProfile = () => { if (scientist?.handle) getScientistProfile(scientist.handle).then(setProfile).catch(() => {}); };
  const onLogged = () => { refreshProfile(); refreshGlobals(); };
  const isOnboarded = !!scientist;
  const requireOnboard = (action) => () => { if (isOnboarded) action(); else setShowOnboard(true); };

  // ----- Admin route render (after all hooks have run, so rules-of-hooks is satisfied) -----
  if (adminMode) {
    return <AdminInbox onExit={() => {
      window.history.replaceState(null, "", window.location.pathname);
      setAdminMode(false);
    }} />;
  }
  if (seanMode) {
    return <SeanStudio onExit={() => {
      window.history.replaceState(null, "", window.location.pathname);
      setSeanMode(false);
    }} />;
  }

  return (
    <div className="min-h-screen pb-10">
      <TopNav
        isOnboarded={isOnboarded}
        scientist={scientist}
        onJoin={() => setShowOnboard(true)}
        onSprint={() => setShowSprint(true)}
        onLeaderboard={() => setShowLeaderboard(true)}
      />

      <TesterCountdownBanner
        onJoin={() => setShowOnboard(true)}
        isOnboarded={isOnboarded}
        role={profile?.scientist?.role || scientist?.role}
      />

      <LabStories />

      <Hero
        stats={globalStats}
        isOnboarded={isOnboarded}
        handle={scientist?.handle}
        onSprint={() => setShowSprint(true)}
        onJoin={() => setShowOnboard(true)}
      />

      <QuotesMarquee />

      {isOnboarded && <YourLabStrip profile={profile} />}

      {dailyMission && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-6">
          <div className="card tint-sun" data-testid="daily-mission-card">
            <div className="flex items-start gap-4">
              <div className="text-5xl animate-float">{dailyMission.badge_emoji}</div>
              <div>
                <div className="eyebrow text-[var(--red)]">today's mission</div>
                <div className="font-display text-2xl text-[var(--ink)] leading-tight mt-1">{dailyMission.title}</div>
                <p className="text-[var(--ink)] mt-2 leading-relaxed">{dailyMission.description}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      <main id="log" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-8">
        {!isOnboarded ? (
          <div className="card text-center max-w-2xl mx-auto tint-cream">
            <div className="text-6xl mb-3 animate-wiggle inline-block">🧪</div>
            <h2 className="font-display text-3xl text-[var(--ink)] mb-2">Ready to start your lab?</h2>
            <p className="text-[var(--ink-soft)] mb-5">Track your Brain × Heart loop AND compete for fastest reader.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button className="btn btn-red" onClick={() => setShowSprint(true)} data-testid="cta-sprint">⚡ Try a Speed Race</button>
              <button className="btn btn-primary" onClick={() => setShowOnboard(true)} data-testid="cta-start-lab">🧪 Start My Lab</button>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-5">
            <div className="lg:col-span-7"><LabNotebookCard scientist={scientist} genres={genres} onLogged={onLogged} /></div>
            <div className="lg:col-span-5 space-y-5">
              <div className="card tint-red text-white" style={{ background: "var(--red)" }}>
                <div className="eyebrow text-[var(--sun)]">🏁 fastest reader</div>
                <div className="font-display text-2xl text-white leading-tight mt-1">Race the clock — earn WPM!</div>
                <p className="text-white opacity-90 mt-2 text-sm leading-relaxed">Pick a passage. Read as fast as you can. Top WPM goes on the leaderboard.</p>
                <button onClick={() => setShowSprint(true)} className="btn btn-primary mt-4" data-testid="lab-sprint">⚡ Start Speed Race</button>
              </div>
              <div className="card tint-blue">
                <div className="eyebrow text-[var(--navy)]">tip from sean</div>
                <div className="font-display text-xl text-[var(--ink)] leading-tight mt-1">"If a fact sounds too wild, fact-check it. Even I got the 4th-floor heart fact wrong once."</div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button onClick={requireOnboard(() => setShowMisinfo(true))} className="btn-ghost text-sm">🔍 Clickbait Slayer</button>
                  <button onClick={requireOnboard(() => setShowAsk(true))} className="btn-ghost text-sm">💬 Ask Sean</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <ToolsSection
        onSprint={() => setShowSprint(true)}
        onAsk={requireOnboard(() => setShowAsk(true))}
        onMisinfo={requireOnboard(() => setShowMisinfo(true))}
        onBooks={() => setShowBooks(true)}
        onLeaderboard={() => setShowLeaderboard(true)}
      />

      {isOnboarded && <FeedAndBadges feed={feed} badges={profile?.badges || []} />}

      <SeansLatestFeed />

      <SeanReels />

      <SeansVideos />

      {/* KEEP ON SCIENCING banner with TED photo */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-12">        <div className="card tint-sun relative overflow-hidden">
          <div className="grid lg:grid-cols-12 gap-5 items-center">
            <div className="lg:col-span-7">
              <div className="eyebrow text-[var(--red)] mb-2">signed · sean the science kid</div>
              <h2 className="font-display text-4xl sm:text-6xl text-[var(--ink)] leading-none mb-3">KEEP ON SCIENCING ⚡</h2>
              <p className="text-[var(--ink)] text-base sm:text-lg max-w-xl">Sean's signature sign-off. Every question counts. Every loop, balanced. Every page, a heartbeat.</p>
              <div className="mt-4 flex flex-wrap gap-2 press-pills">
                <span className="pill pill-red">📺 TED · Re:Thinking</span>
                <span className="pill pill-navy">📰 ABC · CNN · FOX 10</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <img src={SEAN_TED} alt="Sean on TED Re:Thinking with Adam Grant" className="w-full rounded-2xl block" style={{ border: "2.5px solid var(--ink)", boxShadow: "4px 4px 0 0 var(--ink)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* Sean's World — official site + merch */}
      <SeanWorldSection />

      {/* Press / Media Kit */}
      <MediaKitSection />

      {/* Booking Agreement — collapsed at bottom */}
      <BookingSection />

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-10 pt-6" style={{ borderTop: "2.5px solid var(--ink)" }}>
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-3" style={{ height: 56 }}>
            <img src={SEAN_LOGO} alt="Logo" className="h-full w-auto rounded-full border-2 border-[var(--ink)] object-cover" />
            <div>
              <div className="font-display text-[var(--ink)]">Sean The Science Kid</div>
              <div className="label-tag">⚡ {SLOGAN}</div>
            </div>
          </div>
          {isOnboarded && <div className="font-mono text-xs">@{scientist.handle} · <button onClick={() => { localStorage.clear(); window.location.reload(); }} className="underline" data-testid="logout-btn">switch</button></div>}
        </div>
        <div className="text-center text-xs sm:text-sm mt-4 mb-2 no-print" style={{ color: "var(--ink-soft)" }}>
          Fan-built citizen-science platform · video credit to Sean &amp; his channel · <span className="font-mono">@seanthesciencekidofficial</span>
        </div>
        <div className="flex flex-col items-center gap-2 mt-1 mb-3 no-print" data-testid="footer-attribution">
          <span
            className="pill font-mono"
            style={{ background: "var(--navy)", color: "#fff", padding: "0.3rem 0.85rem", letterSpacing: "0.02em", fontSize: "0.7rem" }}
          >
            ✦ Designed by <strong style={{ color: "var(--sun)", marginLeft: 4 }}>NIGHTEENGALE GROUP</strong>
          </span>
          <button
            onClick={() => setShowVault(true)}
            data-testid="footer-vault-trigger"
            className="pill font-mono"
            style={{ background: "var(--ink)", color: "var(--sun)", padding: "0.3rem 0.85rem", letterSpacing: "0.02em", fontSize: "0.7rem", cursor: "pointer", border: "none" }}
            title="For the sharp Sciencers only"
          >
            🔓 The Vault
          </button>
        </div>
      </footer>

      {showOnboard && !isOnboarded && <Onboarding onDone={(s) => { setScientist(s); setShowOnboard(false); getScientistProfile(s.handle).then(setProfile); }} onClose={() => setShowOnboard(false)} />}
      <SpeedReaderModal open={showSprint} onClose={() => setShowSprint(false)} handle={scientist?.handle} />
      <AskSeanModal open={showAsk} onClose={() => setShowAsk(false)} sessionId={sessionId} />
      <MisinfoBusterModal open={showMisinfo} onClose={() => setShowMisinfo(false)} handle={scientist?.handle} />
      <BookRecommenderModal open={showBooks} onClose={() => setShowBooks(false)} />
      <LeaderboardModal open={showLeaderboard} onClose={() => setShowLeaderboard(false)} leaders={leaders} handle={scientist?.handle} />
      <SneakPeekVault open={showVault} onClose={() => setShowVault(false)} handle={scientist?.handle} />

      {/* Floating AI Sean — present on every page state */}
      <SeanChatbot />

      {/* First-visit: Sean asks new visitors to install the app (cheeky) */}
      <FirstVisitInstallPrompt />

      {/* Silent auto-updater — pulls new builds for installed PWAs without user action */}
      <AutoUpdater />
    </div>
  );
}

export default App;
